import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Container,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Box,
    Grid,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { apiClient } from '../services/api'

interface Broker {
    id: string
    name: string
    address1: string | null
    address2: string | null
    city: string | null
    state: string | null
    postalCode: string | null
    country: string | null
    contactFirstName: string | null
    contactLastName: string | null
    contactEmail: string | null
    contactPhone: string | null
    isActive: boolean
}

export default function BrokersPage() {
    const navigate = useNavigate()
    const [brokers, setBrokers] = useState<Broker[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [openDialog, setOpenDialog] = useState(false)
    const [saving, setSaving] = useState(false)
    const [newBroker, setNewBroker] = useState({
        name: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'USA',
        contactFirstName: '',
        contactLastName: '',
        contactEmail: '',
        contactPhone: '',
        isActive: true,
    })

    useEffect(() => {
        fetchBrokers()
    }, [])

    const fetchBrokers = async () => {
        try {
            setLoading(true)
            const data = await apiClient.getBrokers()
            setBrokers(data)
            setError(null)
        } catch (err) {
            setError('Failed to load brokers')
            console.error(err)
        } finally {
            setLoading(false)
        }
    }

    const handleOpenDialog = () => {
        setOpenDialog(true)
    }

    const handleCloseDialog = () => {
        setOpenDialog(false)
        setNewBroker({
            name: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'USA',
            contactFirstName: '',
            contactLastName: '',
            contactEmail: '',
            contactPhone: '',
            isActive: true,
        })
    }

    const handleInputChange = (field: string, value: string) => {
        setNewBroker(prev => ({ ...prev, [field]: value }))
    }

    const handleSaveBroker = async () => {
        if (!newBroker.name.trim()) {
            setError('Broker name is required')
            return
        }

        try {
            setSaving(true)
            setError(null)
            await apiClient.createBroker(newBroker)
            await fetchBrokers()
            handleCloseDialog()
        } catch (err) {
            setError('Failed to create broker')
            console.error(err)
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <Container maxWidth="lg">
                <CircularProgress />
            </Container>
        )
    }

    if (error) {
        return (
            <Container maxWidth="lg">
                <Alert severity="error">{error}</Alert>
            </Container>
        )
    }

    return (
        <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Broker Administration
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                >
                    Add Broker
                </Button>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Contact Name</TableCell>
                            <TableCell>Contact Email</TableCell>
                            <TableCell>Contact Phone</TableCell>
                            <TableCell>City</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {brokers.map(broker => (
                            <TableRow
                                key={broker.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/brokers/${broker.id}`)}
                            >
                                <TableCell>{broker.name}</TableCell>
                                <TableCell>
                                    {broker.contactFirstName || broker.contactLastName
                                        ? `${broker.contactFirstName || ''} ${broker.contactLastName || ''}`.trim()
                                        : '-'}
                                </TableCell>
                                <TableCell>{broker.contactEmail || '-'}</TableCell>
                                <TableCell>{broker.contactPhone || '-'}</TableCell>
                                <TableCell>{broker.city || '-'}</TableCell>
                                <TableCell>{broker.state || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={broker.isActive ? 'Active' : 'Inactive'}
                                        color={broker.isActive ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>Add New Broker</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Broker Name"
                                    value={newBroker.name}
                                    onChange={(e) => handleInputChange('name', e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address Line 1"
                                    value={newBroker.address1}
                                    onChange={(e) => handleInputChange('address1', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Address Line 2"
                                    value={newBroker.address2}
                                    onChange={(e) => handleInputChange('address2', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="City"
                                    value={newBroker.city}
                                    onChange={(e) => handleInputChange('city', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="State"
                                    value={newBroker.state}
                                    onChange={(e) => handleInputChange('state', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    label="Postal Code"
                                    value={newBroker.postalCode}
                                    onChange={(e) => handleInputChange('postalCode', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Country"
                                    value={newBroker.country}
                                    onChange={(e) => handleInputChange('country', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Contact First Name"
                                    value={newBroker.contactFirstName}
                                    onChange={(e) => handleInputChange('contactFirstName', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Contact Last Name"
                                    value={newBroker.contactLastName}
                                    onChange={(e) => handleInputChange('contactLastName', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Contact Email"
                                    value={newBroker.contactEmail}
                                    onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                                    fullWidth
                                    type="email"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Contact Phone"
                                    value={newBroker.contactPhone}
                                    onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSaveBroker} variant="contained" color="primary" disabled={saving}>
                        {saving ? 'Creating...' : 'Create Broker'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
